import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import PrintIcon from "@mui/icons-material/Print";
import RefreshIcon from "@mui/icons-material/Refresh";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  GlobalStyles,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ListItemText from "@mui/material/ListItemText";
import moment from "moment";
import { useCallback, useMemo, useState, type Dispatch, type SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { useGetAggregatorsList } from "src/api/aggreagators";
import { useGetBranches } from "src/api/generic";
import { REPORT_BRANCH_MULTISELECT_ALL_SENTINEL } from "src/constants/reportBranchSelect";
import {
  useGetAggsStatementReport,
  useGetAggregatorStatementReportData,
  type AggregatorStatementReportDataPayload,
  type AggregatorStatementReportRow,
} from "src/api/reports";
import DateRangeInput from "src/shared/components/date-range";
import {
  ReportControlBar,
  ReportPageHeading,
  ReportSectionCard,
  reportLayoutTokens,
} from "src/shared/components/report-layout";
import { authState } from "src/store/auth";
import RightArrow from "src/assets/svg-icons/aggregators/right_arrow.svg";
import {
  downloadAsXLS,
  oneBranchHasPermission,
  specificBranchHasPermission,
} from "src/utils";
import { ReportsPermissions } from "src/constants/permissions";

type AggStatementFilters = {
  startDate: string;
  endDate: string;
  branchIds: number[];
};

const parseAggStatementFilters = (sp: URLSearchParams): AggStatementFilters => {
  const b = sp.getAll("branches");
  return {
    startDate:
      sp.get("start_date") || moment().startOf("month").format("yyyy-MM-DD"),
    endDate:
      sp.get("end_date") || moment().endOf("month").format("yyyy-MM-DD"),
    branchIds: b.length ? b.map((x) => Number(x)).filter((n) => Number.isFinite(n)) : [],
  };
};

const formatStatementNumber = (n: number) =>
  new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);

const AggregatorStatementReportPage = () => {
  const { t } = useTranslation("reports");
  const { t: generalT } = useTranslation("general");
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const auth = useRecoilValue(authState);
  const companyName = auth.user?.company?.name;

  const [appliedFilters, setAppliedFilters] = useState<AggStatementFilters>(() =>
    parseAggStatementFilters(new URLSearchParams(searchParams.toString()))
  );
  const [draftFilters, setDraftFilters] = useState<AggStatementFilters>(() =>
    parseAggStatementFilters(new URLSearchParams(searchParams.toString()))
  );

  const [draftAggregatorId, setDraftAggregatorId] = useState<string>("");
  const [appliedAggregatorId, setAppliedAggregatorId] = useState<string>("");
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [modalAggregatorDraft, setModalAggregatorDraft] = useState<string>("");

  const { startDate, endDate, branchIds } = appliedFilters;

  const { data: branchesData } = useGetBranches();
  const branchesHavePermission =
    branchesData?.data.filter((br) =>
      specificBranchHasPermission(
        ReportsPermissions.viewThirdEyeReportDeliveryApps,
        br.id
      )
    ) ?? [];

  const { data: reportBody, isFetching, refetch } = useGetAggregatorStatementReportData({
    start_date: startDate,
    end_date: endDate,
    branches: branchIds.length > 0 ? branchIds : undefined,
    enabled: true,
  });

  const { refetch: refetchDownload, isFetching: isDownloadLoading } =
    useGetAggsStatementReport({
      start_date: startDate,
      end_date: endDate,
      branches: branchIds.length > 0 ? branchIds : undefined,
      enabled: false,
    });

  const downloadReportsPermission = oneBranchHasPermission(
    ReportsPermissions.viewThirdEyeReportDeliveryApps
  );

  const { data: aggregatorsApiPayload } = useGetAggregatorsList({
    enabled: filterModalOpen,
  });

  const payload = (reportBody as { data?: AggregatorStatementReportDataPayload } | null | undefined)
    ?.data;

  const columns = payload?.columns ?? [];

  /** Live `/api/aggregators` catalog merged with IDs present on the loaded statement so labels stay accurate. */
  const aggregatorOptions = useMemo(() => {
    const map = new Map<number, string>();
    (aggregatorsApiPayload?.data ?? []).forEach((a: { id: number; name: string }) => {
      map.set(Number(a.id), String(a.name ?? ""));
    });
    (payload?.rows ?? []).forEach((r) => {
      map.set(r.aggregator_id, r.aggregator_name);
    });
    return Array.from(map.entries()).sort((a, b) =>
      a[1].localeCompare(b[1], undefined, { sensitivity: "base" })
    );
  }, [aggregatorsApiPayload?.data, payload?.rows]);

  const filteredRows = useMemo(() => {
    const dataRows = payload?.rows ?? [];
    const idStr = appliedAggregatorId.trim();
    if (!idStr) return dataRows;
    const id = Number(idStr);
    if (!Number.isFinite(id)) return dataRows;
    return dataRows.filter((r) => r.aggregator_id === id);
  }, [payload?.rows, appliedAggregatorId]);

  const periodLabel = useMemo(
    () =>
      t("reportDateRange", {
        start: moment(startDate).format("D MMM YYYY"),
        end: moment(endDate).format("D MMM YYYY"),
      }),
    [t, startDate, endDate]
  );

  const openFilterModal = () => {
    setModalAggregatorDraft(draftAggregatorId);
    setFilterModalOpen(true);
  };

  const cancelFilterModal = () => setFilterModalOpen(false);

  const commitFiltersFromModal = () => {
    setDraftAggregatorId(modalAggregatorDraft);
    setFilterModalOpen(false);
  };

  const syncAppliedToUrl = useCallback(
    (next: AggStatementFilters) => {
      const p = new URLSearchParams();
      p.set("start_date", next.startDate);
      p.set("end_date", next.endDate);
      next.branchIds.forEach((id) => p.append("branches", String(id)));
      setSearchParams(p);
    },
    [setSearchParams]
  );

  const handleReportUpdate = () => {
    setAppliedFilters({ ...draftFilters });
    setAppliedAggregatorId(draftAggregatorId.trim());
    syncAppliedToUrl(draftFilters);
    void refetch();
  };

  const handlePrint = () => window.print();

  const handleDownload = useCallback(async () => {
    const result = await refetchDownload();
    const d = result.data as string | undefined;
    if (d != null) {
      downloadAsXLS(`\uFEFF${d}`, "aggregator-statement-report");
    }
  }, [refetchDownload]);

  const cellValue = (args: {
    columnKey: string;
    row: AggregatorStatementReportRow;
  }) => {
    const { columnKey, row } = args;
    const v = row[columnKey as keyof AggregatorStatementReportRow];
    if (columnKey === "aggregator_name") return row.aggregator_name;
    if (typeof v !== "number") return "—";
    if (columnKey === "aggregator_commission_pct") {
      return formatStatementNumber(v);
    }
    return formatStatementNumber(v);
  };

  const minTableWidth = Math.max(columns.length * 130, 900);

  const branchSelectControl = (
    value: AggStatementFilters["branchIds"],
    onChange: (ids: number[]) => void
  ) => (
    <FormControl
      size="small"
      sx={{
        minWidth: 220,
        flexShrink: 0,
        alignSelf: "flex-start",
        "& .MuiOutlinedInput-root": { minHeight: 40 },
      }}
    >
      <InputLabel id="agg-st-branches" shrink>
        {generalT("branch")}
      </InputLabel>
      <Select<string[]>
        labelId="agg-st-branches"
        multiple
        value={value.map((id) => String(id))}
        onChange={(e: SelectChangeEvent<string[]>) => {
          const v = e.target.value;
          const next = typeof v === "string" ? v.split(",") : v;
          const strs = next as string[];
          if (strs.includes(REPORT_BRANCH_MULTISELECT_ALL_SENTINEL)) {
            onChange([]);
            return;
          }
          const ids = strs.map(Number).filter((n) => n > 0);
          onChange(ids);
        }}
        input={<OutlinedInput label={generalT("branch")} />}
        renderValue={(selected) => {
          if (selected.length === 0) return t("allBranches");
          return selected
            .map(
              (idStr) =>
                branchesHavePermission.find((b) => String(b.id) === idStr)?.name ?? idStr
            )
            .join(", ");
        }}
      >
        <MenuItem
          key="__all-branches-agg-st"
          value={REPORT_BRANCH_MULTISELECT_ALL_SENTINEL}
          sx={{
            borderBottom: "1px solid #EAECF0",
            bgcolor: "#F9FAFB",
          }}
        >
          <Typography fontWeight={600} variant="body2" component="span">
            {t("allBranches")}
          </Typography>
        </MenuItem>
        {branchesHavePermission.map((b) => (
          <MenuItem key={b.id} value={String(b.id)}>
            <Checkbox size="small" checked={value.includes(b.id)} />
            <ListItemText primary={b.name} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const dateRangeControl = (
    draft: AggStatementFilters,
    setDraft: Dispatch<SetStateAction<AggStatementFilters>>
  ) => (
    <Box sx={{ minWidth: 250 }}>
      <DateRangeInput
        startDate={draft.startDate ? moment(draft.startDate) : null}
        startDateId="agg-st-start"
        endDate={draft.endDate ? moment(draft.endDate) : null}
        endDateId="agg-st-end"
        onDatesChange={(arg: {
          startDate: moment.Moment | null;
          endDate: moment.Moment | null;
        }) => {
          const s = arg.startDate?.format("yyyy-MM-DD");
          const e = arg.endDate?.format("yyyy-MM-DD");
          if (s && e) {
            setDraft((prev) => ({ ...prev, startDate: s, endDate: e }));
          }
        }}
      />
    </Box>
  );

  return (
    <Box
      p={{ xs: 2, sm: "12px 32px 32px" }}
      maxWidth="100%"
      sx={{ width: "100%", minWidth: 0, boxSizing: "border-box" }}
    >
      <GlobalStyles
        styles={{
          "@media print": {
            "body *": { visibility: "hidden" },
            "#agg-statement-print-root, #agg-statement-print-root *": {
              visibility: "visible",
            },
            "#agg-statement-print-root": {
              position: "absolute",
              left: 0,
              top: 0,
              width: "100%",
            },
          },
        }}
      />

      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        mb={1.5}
        sx={{ "@media print": { display: "none" } }}
      >
        <Typography
          fontSize="0.9rem"
          color="#475467"
          onClick={() => navigate("/reports")}
          sx={{ cursor: "pointer" }}
        >
          {t("thirdEyeReport")}
        </Typography>
        <img src={RightArrow} alt="" style={{ width: 12, height: 12 }} />
        <Box borderRadius="4px" bgcolor="#EAECF0" px={1} py={0.5}>
          <Typography color="#344054" fontWeight={600} fontSize="0.85rem">
            {t("aggsStatementReport")}
          </Typography>
        </Box>
      </Stack>

      <Box sx={{ "@media print": { display: "none" } }} mb={2}>
        <ReportControlBar
          left={
            <>
              {branchSelectControl(draftFilters.branchIds, (ids) =>
                setDraftFilters((p) => ({ ...p, branchIds: ids }))
              )}
              {dateRangeControl(draftFilters, setDraftFilters)}
            </>
          }
          center={
            <>
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<FilterListIcon fontSize="small" />}
                onClick={openFilterModal}
                sx={{
                  height: 40,
                  minWidth: 100,
                  borderColor: "#D0D5DD",
                  color: "#344054",
                }}
              >
                {t("filter")}
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<RefreshIcon fontSize="small" />}
                onClick={handleReportUpdate}
                disabled={isFetching}
                sx={{ height: 40, minWidth: 120 }}
              >
                {t("reportUpdate")}
              </Button>
            </>
          }
          right={
            <>
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<PrintIcon fontSize="small" />}
                onClick={handlePrint}
                sx={{
                  height: 40,
                  minWidth: 100,
                  borderColor: "#D0D5DD",
                  color: "#344054",
                }}
              >
                {t("print")}
              </Button>
              <LoadingButton
                variant="outlined"
                startIcon={<FileDownloadIcon fontSize="small" />}
                onClick={handleDownload}
                loading={isDownloadLoading}
                disabled={!downloadReportsPermission}
                sx={{
                  height: 40,
                  minWidth: 100,
                  borderColor: "#D0D5DD",
                  color: "#344054",
                }}
              >
                {t("export")}
              </LoadingButton>
            </>
          }
        />
      </Box>

      <Dialog open={filterModalOpen} onClose={cancelFilterModal} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pr: 1 }}>
          <FilterListIcon sx={{ color: "#475467", fontSize: 22 }} />
          <Typography component="span" fontWeight={700} flex={1}>
            {t("filter")}
          </Typography>
          <IconButton aria-label="close" onClick={cancelFilterModal} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="agg-st-modal-aggregator" shrink>
              {t("filterModalAggregatorName")}
            </InputLabel>
            <Select<string>
              labelId="agg-st-modal-aggregator"
              label={t("filterModalAggregatorName")}
              value={modalAggregatorDraft}
              displayEmpty
              onChange={(e) =>
                setModalAggregatorDraft(
                  typeof e.target.value === "string" ? e.target.value : ""
                )
              }
              input={
                <OutlinedInput notched label={t("filterModalAggregatorName")} />
              }
              renderValue={(selected) => {
                if (!selected) return t("filterModalAggregatorName");
                const id = Number(selected);
                const name = aggregatorOptions.find(([aggId]) => aggId === id)?.[1];
                return name ?? selected;
              }}
              sx={{
                borderRadius: "8px",
                "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
              }}
            >
              <MenuItem value="">
                <em>{t("filterModalAggregatorName")}</em>
              </MenuItem>
              {aggregatorOptions.map(([id, name]) => (
                <MenuItem key={id} value={String(id)}>
                  <Box component="span" dir="auto">
                    {name}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, flexDirection: "column", gap: 1 }}>
          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={commitFiltersFromModal}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {t("applyFilters")}
          </Button>
          <Button color="inherit" fullWidth onClick={cancelFilterModal} sx={{ textTransform: "none" }}>
            {generalT("cancel")}
          </Button>
        </DialogActions>
      </Dialog>

      <Box id="agg-statement-print-root">
        <ReportPageHeading
          title={t("aggsStatementReport")}
          subtitle={companyName ?? payload?.company_name}
          periodLabel={periodLabel}
        />

        <ReportSectionCard>
          {isFetching && !payload ? (
            <Box display="flex" justifyContent="center" py={8}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer
              sx={{
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
                overflowX: "auto",
                WebkitOverflowScrolling: "touch",
              }}
            >
              <Table size="small" sx={{ minWidth: minTableWidth, tableLayout: "auto" }}>
                <TableHead>
                  <TableRow
                    sx={{
                      "& th": {
                        bgcolor: reportLayoutTokens.tableHeaderBg,
                        color: reportLayoutTokens.tableHeaderText,
                        fontWeight: 700,
                        fontSize: "0.7rem",
                        textTransform: "uppercase",
                        borderBottom: reportLayoutTokens.cardBorder,
                        py: 1.25,
                        whiteSpace: "nowrap",
                      },
                    }}
                  >
                    {columns.map((col) => (
                      <TableCell
                        key={col.key}
                        align={col.key === "aggregator_name" ? "left" : "center"}
                      >
                        {col.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody sx={{ position: "relative", zIndex: 1 }}>
                  {filteredRows.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={Math.max(columns.length, 1)}
                        align="center"
                        sx={{ py: 4, color: "text.secondary", fontSize: "0.9rem" }}
                      >
                        {generalT("noData")}
                      </TableCell>
                    </TableRow>
                  ) : null}
                  {filteredRows.length > 0 &&
                    filteredRows.map((row, idx) => (
                      <TableRow
                        key={row.aggregator_id ?? `${row.aggregator_name}-${idx}`}
                        sx={{
                          "& td": {
                            borderColor: "#EEF0F2",
                            fontSize: "0.875rem",
                            color: "#101828",
                            py: 1,
                          },
                        }}
                      >
                        {columns.map((col) => (
                          <TableCell
                            key={col.key}
                            align={
                              col.key === "aggregator_name" ? "left" : "center"
                            }
                            sx={{
                              fontWeight:
                                col.key === "aggregator_name" ? 500 : 600,
                            }}
                          >
                            {cellValue({ columnKey: col.key, row })}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </ReportSectionCard>
      </Box>
    </Box>
  );
};

export default AggregatorStatementReportPage;
